// Signed-out account flows: forgot password, reset from an emailed link, verify an email address.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { CheckCircle2, Loader2, MailCheck, TriangleAlert } from "lucide-react";
import { Button, Input, PasswordInput } from "@atrix-analytics/ui";
import { api, USE_FIXTURES } from "../api";
import { ApiError, errorMessage } from "../api/http";
import { keys, meQuery } from "../api/queries";
import { ForgotPasswordInput, ResetPasswordInput } from "../api/schemas";
import { demoMailbox } from "../api/fixtures/mailbox";
import { Field, FormError } from "../components/field";
import { AuthShell, fieldErrors, type Errors } from "./auth";

/** In demo mode the "email" lands in this browser; offer its link so the flow can be finished. */
export function DemoMailLink({ kind }: { kind: "verify_email" | "password_reset" }) {
  const mail = USE_FIXTURES ? demoMailbox.last() : null;
  if (!mail || mail.kind !== kind) return null;
  return (
    <p className="rounded-md border border-dashed border-border-strong px-3 py-2 text-xs text-fg-muted">
      Demo mode: no email is really sent.{" "}
      <Link to={mail.path as "/"} className="font-medium text-accent underline-offset-4 hover:underline">
        Open the link that would have arrived
      </Link>
    </p>
  );
}

export function ForgotPasswordPage() {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useMutation({
    mutationFn: api.requestPasswordReset,
    onSuccess: (_, input) => setSentTo(input.email),
    // The endpoint is always 202; only rate limits and outages get here.
    onError: (err) => err instanceof ApiError && setErrors(err.fields),
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = ForgotPasswordInput.safeParse(Object.fromEntries(new FormData(e.currentTarget)));
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    mutation.mutate(parsed.data);
  };

  if (sentTo) {
    return (
      <AuthShell title="Check your email" subtitle={<>If {sentTo} has an account, a reset link is on its way. It works for one hour.</>}>
        <div className="flex flex-col gap-4">
          <p className="flex items-start gap-2 text-sm text-fg-muted">
            <MailCheck aria-hidden className="mt-0.5 size-4 shrink-0 text-ok" />
            Nothing after a few minutes? Check spam, or try again with the address you signed up with.
          </p>
          <DemoMailLink kind="password_reset" />
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setSentTo(null)}>
              Use another address
            </Button>
            <Link
              to="/sign-in"
              search={{}}
              className="inline-flex h-8.5 items-center px-2 text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Reset your password"
      subtitle={
        <>
          We'll email you a link to choose a new one. Remembered it?{" "}
          <Link to="/sign-in" search={{}} className="font-medium text-accent underline-offset-4 hover:underline">
            Sign in
          </Link>
        </>
      }
    >
      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <FormError message={mutation.isError && !Object.keys(errors).length ? errorMessage(mutation.error) : null} />
        <Field label="Work email" error={errors.email}>
          <Input name="email" type="email" autoComplete="email" inputMode="email" required autoFocus />
        </Field>
        <Button type="submit" size="lg" className="mt-2 w-full" disabled={mutation.isPending}>
          {mutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
          {mutation.isPending ? "Sending…" : "Send reset link"}
        </Button>
      </form>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { token } = useSearch({ from: "/reset-password" });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useMutation({
    mutationFn: api.resetPassword,
    onSuccess: () => {
      // A reset signs out every session, including any in this browser.
      qc.setQueryData(keys.me, null);
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fields),
  });

  if (!token) {
    return (
      <AuthShell title="This link is incomplete" subtitle="The reset link is missing its code. Open it again from the email, or ask for a new one.">
        <Link to="/forgot-password" className="text-sm font-medium text-accent underline-offset-4 hover:underline">
          Send a new reset link
        </Link>
      </AuthShell>
    );
  }

  if (mutation.isSuccess) {
    return (
      <AuthShell title="Password changed" subtitle="Every session was signed out, so sign in again with your new password.">
        <Button size="lg" className="w-full" onClick={() => navigate({ to: "/sign-in", search: {} })}>
          Sign in
        </Button>
      </AuthShell>
    );
  }

  const expired = mutation.error instanceof ApiError && (mutation.error.code === "invalid_token" || mutation.error.status === 404);
  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = Object.fromEntries(new FormData(e.currentTarget));
    if (data.new_password !== data.confirm) return setErrors({ confirm: "The two passwords don't match" });
    const parsed = ResetPasswordInput.safeParse({ token, new_password: data.new_password });
    if (!parsed.success) return setErrors(fieldErrors(parsed.error.issues));
    setErrors({});
    mutation.mutate(parsed.data);
  };

  return (
    <AuthShell title="Choose a new password" subtitle="At least 10 characters. Every other session will be signed out.">
      {expired ? (
        <div className="flex flex-col gap-3">
          <FormError message="This link has expired or was already used." />
          <Link to="/forgot-password" className="text-sm font-medium text-accent underline-offset-4 hover:underline">
            Send a new reset link
          </Link>
        </div>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          <FormError message={mutation.isError && !Object.keys(errors).length ? errorMessage(mutation.error) : null} />
          <Field label="New password" error={errors.new_password} hint="At least 10 characters.">
            <PasswordInput name="new_password" autoComplete="new-password" minLength={10} required autoFocus />
          </Field>
          <Field label="Repeat new password" error={errors.confirm}>
            <PasswordInput name="confirm" autoComplete="new-password" required />
          </Field>
          <Button type="submit" size="lg" className="mt-2 w-full" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="animate-spin" aria-hidden />}
            {mutation.isPending ? "Saving…" : "Set new password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function VerifyEmailPage() {
  const { token } = useSearch({ from: "/verify-email" });
  const qc = useQueryClient();
  const me = useQuery(meQuery);
  const ran = useRef(false);
  const mutation = useMutation({
    mutationFn: (t: string) => api.verifyEmail(t),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
  // Verify once on arrival (StrictMode runs effects twice in dev).
  useEffect(() => {
    if (token && !ran.current) {
      ran.current = true;
      mutation.mutate(token);
    }
  }, [token, mutation]);

  const next = me.data ? (
    <Link to="/" className="inline-flex h-10 w-full items-center justify-center rounded-md bg-accent px-5 text-sm font-medium text-accent-fg hover:bg-accent-hover">
      Continue to your projects
    </Link>
  ) : (
    <Link to="/sign-in" search={{}} className="inline-flex h-10 w-full items-center justify-center rounded-md bg-accent px-5 text-sm font-medium text-accent-fg hover:bg-accent-hover">
      Sign in
    </Link>
  );

  if (!token || mutation.isError) {
    return (
      <AuthShell
        title="We couldn't verify that link"
        subtitle={
          !token
            ? "The link is missing its code. Open it again from the email."
            : mutation.error instanceof ApiError && mutation.error.status === 404
              ? "It has expired or was already used. Send a fresh one from the banner in the console."
              : errorMessage(mutation.error)
        }
      >
        <p className="mb-4 flex items-center gap-2 text-sm text-fg-muted">
          <TriangleAlert aria-hidden className="size-4 text-warn" /> Your email address is still unverified.
        </p>
        {next}
      </AuthShell>
    );
  }
  if (mutation.isSuccess) {
    return (
      <AuthShell title="Email verified" subtitle="Thanks — your address is confirmed.">
        <p className="mb-4 flex items-center gap-2 text-sm text-fg-muted">
          <CheckCircle2 aria-hidden className="size-4 text-ok" /> You'll get security notices and reports here.
        </p>
        {next}
      </AuthShell>
    );
  }
  return (
    <AuthShell title="Verifying your email…" subtitle="This only takes a moment.">
      <p role="status" className="flex items-center gap-2 text-sm text-fg-muted">
        <Loader2 aria-hidden className="size-4 animate-spin" /> Checking the link
      </p>
    </AuthShell>
  );
}
